<script lang="ts">
  import { BALANCE } from '@2k48/shared';
  import { createMotionScope, dur, gsap, prefersReducedMotion } from '../../game/motion';

  /**
   * Texto que ondula, letra a letra.
   *
   * Se reserva para lo que celebra algo —el logo, un récord, lo alto de una
   * tabla—: si ondulara cualquier rótulo dejaría de señalar nada y sería sólo
   * ruido.
   *
   * Anima `transform`, nunca una propiedad de layout, así que la ola no puede
   * mover lo que tiene alrededor ni provocar un reflow por fotograma. Y crea su
   * propio `gsap.context()`, que se revierte al desmontar: sin eso, una vista
   * abandonada dejaría un bucle latiendo contra nodos ya descartados.
   */

  interface Props {
    text: string;
    /** Etiqueta del elemento; `h1`, `span`… según dónde se use. */
    as?: 'span' | 'h1' | 'strong';
    class?: string;
    /** Permite que el contenedor conserve metadatos visuales, como el resultado. */
    'data-outcome'?: string;
  }

  let { text, as = 'span', class: className = '', 'data-outcome': outcome }: Props = $props();

  const letters = $derived([...text]);

  function wave(node: HTMLElement) {
    const scope = createMotionScope(node);

    scope.add(() => {
      // La preferencia de menos animación no se negocia: aquí simplemente no
      // hay ola, y el texto se queda quieto y legible.
      if (prefersReducedMotion()) return;

      const glyphs = node.querySelectorAll<HTMLElement>('[data-wave-letter]');
      if (glyphs.length === 0) return;

      const { motion } = BALANCE;
      const fontSize = Number.parseFloat(getComputedStyle(node).fontSize);
      const amplitude = fontSize * motion.ui.waveAmplitudeEm;
      const halfCycle = dur(motion.ui.waveSec) / 2;

      // Un oscilador infinito por letra evita la pausa que producía un único
      // timeline escalonado. Los desfases negativos arrancan ya dentro de la
      // onda, sin una primera pasada donde las letras esperan su turno.
      glyphs.forEach((glyph, index) => {
        gsap.to(glyph, {
          y: -amplitude,
          duration: halfCycle,
          ease: 'sine.inOut',
          repeat: -1,
          yoyo: true,
          delay: -index * motion.ui.waveStaggerSec,
        });
      });
    });

    return { destroy: () => scope.destroy() };
  }
</script>

<!--
  Cada letra en su propio elemento porque una ola necesita desfase entre ellas,
  y eso obliga a que sean nodos separados. Los espacios se preservan con
  `white-space: pre` en el contenedor para que el texto no se descomponga.

  `aria-label` con la cadena entera y las letras ocultas: partido en veinte
  nodos, un lector de pantalla lo deletrearía.
-->
<svelte:element
  this={as}
  class="wave {className}"
  aria-label={text}
  data-outcome={outcome}
  use:wave
>
  {#each letters as letter, index (index)}
    <span class="wave__letter" data-wave-letter aria-hidden="true">{letter}</span>
  {/each}
</svelte:element>
